import { z } from 'zod'

import { ensureRouteData } from '@/app/query-client'
import { fundStockCountQueryOptions } from '@/features/mutual-funds/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { pagingParams } from '@/screens/mutual-funds/search'

/** The two columns the table sorts by: stocks held (the default) and portfolio value. */
export const RANKING_SORTS = ['stocks', 'value'] as const
export const rankingSortSchema = z.enum(RANKING_SORTS)
export type RankingSort = z.infer<typeof rankingSortSchema>

/**
 * `?page=&pageSize=&sortBy=&sortDesc=` — the table sorts and pages a list
 * already in hand (every fund arrives in one response), so both are client-side
 * and none of the four keys a query. No `sortBy` is the opening view: most
 * stocks held first.
 */
export const rankingsSearchSchema = z.object({
  ...pagingParams,
  sortBy: rankingSortSchema.optional().catch(undefined),
  sortDesc: z.boolean().optional().catch(undefined),
})
export type RankingsSearch = z.infer<typeof rankingsSearchSchema>

/**
 * The `/mutual-funds/rankings` loader.
 *
 * MAIN, awaited: the stock-count list — the whole table. It is not keyed on a
 * search param (paging is client-side), so the screen reads it with suspense
 * and a pager click never re-runs this. The summary is not read: the screen
 * no longer prints the month beside the title, and a summary that failed would
 * take the whole page down for a figure nothing draws.
 *
 * `null` period: the backend's latest, the same default Analytics reads, so
 * the two pages share this cache entry.
 */
export async function loadRankings({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<void> {
  await ensureRouteData(queryClient, fundStockCountQueryOptions(null))
}
